import type { DateTime } from "luxon";
import { isInPTO } from "./dateUtils";
import { deriveNurseStatusAtDate, getNurseStatusEndDate } from "./formatters";
import { type BaseNurseShift, DAYS_PER_WEEK, EffectiveEmployeeStatus, type Nurse, type SpecificPreference, type WeekData } from "./models";

/**
 * Mirrors backend `allowed_shift_overlap` (backend/utils.py). The publish route auto-trims an existing
 * shift that overlaps a new one by at most this much (`insert_nurses_to_shift`,
 * backend/database/staffing.py) and rejects anything longer with 409 `error.shift.already.exists`, so
 * for a scheduled add only an overlap strictly longer than this is a hard conflict.
 */
export const ALLOWED_SHIFT_OVERLAP_MS = 60 * 60 * 1000;

/** Which of the nurse's shifts an add collides with: `actual` reads `nursesActual` (actuals mode), `scheduled` reads `nurses`. */
export type ShiftSource = "scheduled" | "actual";

/**
 * How much overlap with an existing shift the publish route accepts, per source. Scheduled adds get the
 * backend's ≤ 1 h auto-trim. Actual adds get none: `insert_nurses_to_shift` skips the trim for
 * `nurse_in_shift_actual`, whose nurse-wide `unique_nurse_shift_actual` EXCLUDE (tstzrange &&) constraint
 * rejects ANY positive overlap — and one rejected insert fails the whole multi-add publish.
 */
export const SHIFT_OVERLAP_TOLERANCE_MS: Readonly<Record<ShiftSource, number>> = { scheduled: ALLOWED_SHIFT_OVERLAP_MS, actual: 0 };

const MS_PER_MINUTE = 60 * 1000;

/** The shift being staffed. */
export type ShiftAvailabilityWindow = { start: DateTime; end: DateTime };

/** The part of an existing shift the overlap rule reads; both scheduled and actual shifts satisfy it. */
export type CandidateShift = Pick<BaseNurseShift, "startTime" | "endTime">;

export type ShiftAvailabilityContext = {
	departmentId: string;
	/** Department `dayStart`, used by the PTO check. */
	dayStart: string;
	/** The nurse's time off, from `useNurseTimeOff`. */
	timeOff: SpecificPreference[];
	/** The staffing day the shift belongs to — status windows and deactivation are day-grained. */
	staffingDate: DateTime;
	/**
	 * The nurse's own same-department shifts that could collide with the window — build it with
	 * `collectNurseShiftsAroundDay`. In actuals mode pass the nurse's actual shifts: an actual insert only
	 * collides with other actuals (a separate table), so scheduled shifts must not block an actual add.
	 */
	candidateShifts: readonly CandidateShift[];
	/** Overlap the publish still accepts, in ms — `SHIFT_OVERLAP_TOLERANCE_MS[source]` for the same source as `candidateShifts`. */
	overlapToleranceMs: number;
	/** Predicted overtime hours from the cost prediction; `null` when no prediction exists (e.g. wages hidden). */
	predictedOvertimeHours: number | null;
	/** Hours the nurse would work on the staffing day INCLUDING this shift. */
	dailyHours: number;
	/** Resolved long-day limit (the same number the daily-hours warning dialog uses). */
	longShiftDurationHours: number;
	/** `getNurseShiftOccurrencesByWeek` output for the shift's week. */
	weeklyShiftOccurrences: Readonly<Record<number, number>>;
};

export type ShiftUnavailableReason =
	| { kind: "leaves_department"; deactivationDate: DateTime }
	/** `endDate` is the stored window end, exclusive — the first day back — or `null` when open-ended. */
	| { kind: "on_leave"; endDate: DateTime | null }
	| { kind: "suspended"; endDate: DateTime | null }
	| { kind: "already_on_shift"; shiftStart: DateTime; shiftEnd: DateTime; isExactShift: boolean; overlapMinutes: number };

export type ShiftAvailabilityBadge =
	| { kind: "pto" }
	| { kind: "overtime"; hours: number }
	| { kind: "long_day"; hours: number; limitHours: number }
	| { kind: "fully_booked"; shiftCount: number; allowedShifts: number };

export type ShiftAvailability =
	| { status: "available"; reason?: undefined; badges: ShiftAvailabilityBadge[] }
	| { status: "blocked"; reason: ShiftUnavailableReason; badges: ShiftAvailabilityBadge[] };

const overlapMs = (a: ShiftAvailabilityWindow, b: CandidateShift): number =>
	Math.max(0, Math.min(a.end.toMillis(), b.endTime.toMillis()) - Math.max(a.start.toMillis(), b.startTime.toMillis()));

const isSameShift = (window: ShiftAvailabilityWindow, shift: CandidateShift): boolean =>
	shift.startTime.toMillis() === window.start.toMillis() && shift.endTime.toMillis() === window.end.toMillis();

/**
 * The first existing shift the publish would reject: overlapping by more than `toleranceMs`. Zero-length
 * shifts are skipped exactly as the backend skips them (a missing-punch actual or a trimmed-away row is
 * not a real booking, and an empty range never overlaps). An exact duplicate blocks even within the
 * tolerance: the backend would trim the existing row to nothing, which is never what "add" means.
 */
const findBlockingShift = (window: ShiftAvailabilityWindow, shifts: readonly CandidateShift[], toleranceMs: number): ShiftUnavailableReason | undefined => {
	for (const shift of shifts) {
		if (shift.startTime.toMillis() === shift.endTime.toMillis()) continue;
		const overlap = overlapMs(window, shift);
		const isExactShift = isSameShift(window, shift);
		if (overlap > toleranceMs || isExactShift) {
			return { kind: "already_on_shift", shiftStart: shift.startTime, shiftEnd: shift.endTime, isExactShift, overlapMinutes: overlap / MS_PER_MINUTE };
		}
	}
	return undefined;
};

const findStatusConflict = (nurse: Nurse, ctx: ShiftAvailabilityContext): ShiftUnavailableReason | undefined => {
	const status = deriveNurseStatusAtDate(nurse, ctx.departmentId, ctx.staffingDate);
	if (status === EffectiveEmployeeStatus.OnLeave) return { kind: "on_leave", endDate: getNurseStatusEndDate(nurse, ctx.departmentId) };
	if (status === EffectiveEmployeeStatus.Suspended) return { kind: "suspended", endDate: getNurseStatusEndDate(nurse, ctx.departmentId) };
	return undefined;
};

/** Same boundary as the grid's unselectable rule: deactivating ON the shift date still allows the shift. */
const findDeactivationConflict = (nurse: Nurse, ctx: ShiftAvailabilityContext): ShiftUnavailableReason | undefined => {
	const deactivationDate = nurse.scheduledDeactivationDate;
	if (deactivationDate && deactivationDate < ctx.staffingDate.startOf("day")) return { kind: "leaves_department", deactivationDate };
	return undefined;
};

/**
 * Does the nurse have time off during the picked hours? THE PTO test for Add Staff — the row badge, the
 * single add's PTO dialog and the multi-add review's "On PTO" group all call it, so they can never
 * disagree. Tested against the window itself (not the staffing day), so a morning PTO doesn't flag a
 * night shift on the same date.
 */
export const isOnPtoDuringWindow = (timeOff: SpecificPreference[], window: ShiftAvailabilityWindow, dayStart: string): boolean =>
	timeOff.some((pref) => isInPTO(pref, window.start, dayStart, window.end));

const collectBadges = (nurse: Nurse, window: ShiftAvailabilityWindow, ctx: ShiftAvailabilityContext): ShiftAvailabilityBadge[] => {
	const badges: ShiftAvailabilityBadge[] = [];
	if (isOnPtoDuringWindow(ctx.timeOff, window, ctx.dayStart)) {
		badges.push({ kind: "pto" });
	}
	if (ctx.predictedOvertimeHours !== null && ctx.predictedOvertimeHours > 0) {
		badges.push({ kind: "overtime", hours: ctx.predictedOvertimeHours });
	}
	if (ctx.dailyHours > ctx.longShiftDurationHours) {
		badges.push({ kind: "long_day", hours: ctx.dailyHours, limitHours: ctx.longShiftDurationHours });
	}
	// DEFAULT: getNurseShiftOccurrencesByWeek only lists nurses with at least one shift that week, so a
	// missing key genuinely means zero shifts — not missing data.
	const shiftCount = ctx.weeklyShiftOccurrences[nurse.id] ?? 0;
	// A non-positive quota is not a real cap; badging every add for it would be noise.
	if (nurse.allowedShifts > 0 && shiftCount >= nurse.allowedShifts) {
		badges.push({ kind: "fully_booked", shiftCount, allowedShifts: nurse.allowedShifts });
	}
	return badges;
};

/**
 * Can this nurse take this shift? The single source for the Add Staff "can't work this shift" bucket,
 * the per-row soft badges, and the multi-add review dialog.
 *
 * Hard conflicts (blocked) are only what the backend rejects or what the grid already refuses; they
 * are checked most-permanent first so a row shows the reason that would still hold on another shift:
 * leaving the department → On Leave / Suspended → already on an overlapping shift. Soft badges (PTO,
 * OT, long day, fully booked) never block — the warning dialogs still confirm them. Cross-department
 * overlaps are invisible here; the backend rejects those and the error surfaces as today.
 *
 * Returns structured kinds + params, never translated copy: the UI owns wording.
 */
export const resolveShiftAvailability = (nurse: Nurse, window: ShiftAvailabilityWindow, ctx: ShiftAvailabilityContext): ShiftAvailability => {
	const badges = collectBadges(nurse, window, ctx);
	const reason =
		findDeactivationConflict(nurse, ctx) ?? findStatusConflict(nurse, ctx) ?? findBlockingShift(window, ctx.candidateShifts, ctx.overlapToleranceMs);
	return reason ? { status: "blocked", reason, badges } : { status: "available", badges };
};

export type CollectNurseShiftsArgs = {
	weeks: readonly WeekData[];
	nurseId: number;
	/** Data-week of the target day (the per-cell index, not the global one — spillover cells differ). */
	weekIndex: number;
	/** 0..6 index into `weeklyShifts[n].days`. */
	dayNo: number;
	source: ShiftSource;
};

/** Day before and after too: an overnight shift on either neighbour can reach into the target day. */
const ADJACENT_DAY_OFFSETS = [-1, 0, 1] as const;

const shiftsOnDay = (week: WeekData, dayNo: number, nurseId: number, source: ShiftSource): BaseNurseShift[] =>
	week.weeklyShifts.flatMap((shift) => {
		const day = shift.days[dayNo];
		// DEFAULT: the backend omits days with no staffing row, so the loaded range's edge week can be short —
		// a missing neighbour day is outside what's loaded, not "no shifts". See UNLOADED_NEIGHBOUR below.
		if (!day) return [];
		// DEFAULT: `nursesActual` is omitted for days with no punches, so absence means "no actual shifts".
		const rows: BaseNurseShift[] = source === "actual" ? (day.nursesActual ?? []) : day.nurses;
		return rows.filter((row) => row.nurseId === nurseId && !row.isPendingToDelete);
	});

/**
 * The nurse's shifts on the target day and both neighbours, crossing into the previous / next data-week
 * at the edges. Pending-to-delete rows are excluded: they are on their way out and must not block the
 * replacement. `hospitalData` is already scoped to one department, which is what makes this "same
 * department" like the backend check.
 *
 * UNLOADED_NEIGHBOUR — a neighbour day outside the loaded weeks contributes nothing. That is "unknown",
 * not "free", but only the first loaded day (the start of the history window) and the last one (the
 * predictor's horizon) have such a neighbour, and only a shift spanning the midnight between the two days
 * (the neighbour's or the picked one) can collide across it. A per-row "not fully checked" badge would mark EVERY candidate on those days for a conflict almost none
 * of them have, so we don't flag it: the publish route's overlap check is the backstop, and a rejected
 * add surfaces its error snackbar with the panel and picks kept for another try.
 */
export const collectNurseShiftsAroundDay = ({ weeks, nurseId, weekIndex, dayNo, source }: CollectNurseShiftsArgs): BaseNurseShift[] =>
	ADJACENT_DAY_OFFSETS.flatMap((offset) => {
		const absoluteDay = weekIndex * DAYS_PER_WEEK + dayNo + offset;
		// DEFAULT: before the first loaded week — UNLOADED_NEIGHBOUR above.
		if (absoluteDay < 0) return [];
		const week = weeks[Math.floor(absoluteDay / DAYS_PER_WEEK)];
		// DEFAULT: after the last loaded week — UNLOADED_NEIGHBOUR above.
		if (!week) return [];
		return shiftsOnDay(week, absoluteDay % DAYS_PER_WEEK, nurseId, source);
	});
